import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ForecastCard, { stageBadgeClass } from "@/components/feature/ForecastCard";
import Reveal from "@/components/base/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { forecasts } from "@/mocks/forecast";

export default function ForecastDetail() {
  const { id } = useParams();
  const item = forecasts.find((entry) => String(entry.id) === id);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, Math.max(0, (window.scrollY / total) * 100)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const related = item
    ? forecasts.filter((entry) => entry.categoryId === item.categoryId && entry.id !== item.id).slice(0, 2)
    : [];
  const fallbackRelated = item ? forecasts.filter((entry) => entry.id !== item.id).slice(0, 2) : [];
  const relatedList = related.length >= 2 ? related : fallbackRelated;

  usePageMeta({
    title: item
      ? `${item.title} - 看料大瓜预告前瞻`
      : "前瞻详情 - 看料大瓜",
    description: item
      ? `${item.excerpt} 看料大瓜为你整理该事件的信号清单与时间线，帮助你提前判断事件走向。`
      : "看料大瓜预告前瞻详情。",
    keywords: item
      ? `${item.tags.join(",")},看料大瓜,预告前瞻,瓜田预警`
      : "看料大瓜,预告前瞻",
    path: `/forecast/${id}`,
    type: "article",
    image: item?.cover,
    robots: item ? "index,follow" : "noindex,follow",
    jsonLd: item
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: item.title,
            description: item.excerpt,
            image: item.cover,
            datePublished: item.date,
            dateModified: item.date,
            articleSection: item.categoryName,
            author: { "@type": "Organization", name: "看料大瓜" },
            publisher: { "@type": "Organization", name: "看料大瓜" },
            keywords: item.tags.join(","),
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: "预告前瞻", item: "/forecast" },
              { "@type": "ListItem", position: 3, name: item.title, item: `/forecast/${item.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!item) {
    return (
      <section className="bg-background-50">
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
            <i className="ri-file-search-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这条前瞻已经被移除了</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            它可能已经更新为新的条目，或者链接已经失效。回到列表页继续浏览其他正在发酵的信号吧。
          </p>
          <Link
            to="/forecast"
            className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回前瞻列表
          </Link>
        </div>
      </section>
    );
  }

  const hotList = [...forecasts].sort((a, b) => b.heat - a.heat).slice(0, 5);

  return (
    <>
      <div className="fixed left-0 top-0 z-[60] h-0.5 w-full bg-transparent">
        <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
      </div>

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs
            items={[{ label: "预告前瞻", to: "/forecast" }, { label: item.title }]}
          />
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Link
              to={`/forecast?category=${item.categoryId}`}
              className="rounded-full bg-background-50 px-3 py-1 text-xs font-semibold text-foreground-800 transition-colors hover:text-primary-600"
            >
              {item.categoryName}
            </Link>
            <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${stageBadgeClass(item.stageTone)}`}>
              {item.stage}
            </span>
          </div>
          <h1 className="mt-4 max-w-4xl font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl">
            {item.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">{item.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {item.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {item.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              阅读约 {item.readTime} 分钟
            </span>
            <span className="flex items-center gap-1.5 font-semibold text-primary-600">
              <i className="ri-fire-line" />
              热度 {item.heat}
            </span>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <article className="lg:col-span-8">
              <div className="overflow-hidden rounded-lg border border-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} 前瞻配图`}
                  title={`${item.title} - 看料大瓜预告前瞻`}
                  className="h-64 w-full object-cover object-top md:h-96"
                />
              </div>

              <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-primary-900">
                  <i className="ri-alarm-warning-line text-lg" />
                  信号清单
                </h2>
                <p className="mt-2 text-xs text-primary-800">
                  以下是我们监测到的异常动向，每条都可以在公开渠道回溯。
                </p>
                <ul className="mt-4 space-y-3">
                  {item.signals.map((signal) => (
                    <li key={signal} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-800">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                      {signal}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">事件时间线</h2>
                <ol className="mt-5 space-y-5">
                  {item.timeline.map((step, index) => (
                    <li key={step.time} className="relative flex gap-4">
                      <div className="flex flex-col items-center">
                        <span className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full border-2 border-primary-500 bg-background-50" />
                        {index < item.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-background-300" />}
                      </div>
                      <div className="pb-1">
                        <p className="text-xs font-semibold text-primary-600">{step.time}</p>
                        <p className="mt-1 text-sm leading-relaxed text-foreground-700">{step.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-8">
                {item.body.map((block, index) => {
                  if (block.type === "h3") {
                    return (
                      <h3
                        key={`${block.type}-${index}`}
                        className="mt-8 font-heading text-xl font-bold text-foreground-950 md:text-2xl"
                      >
                        {block.text}
                      </h3>
                    );
                  }
                  if (block.type === "quote") {
                    return (
                      <blockquote
                        key={`${block.type}-${index}`}
                        className="my-6 border-l-2 border-accent-500 bg-accent-50 px-5 py-4 font-heading text-base leading-relaxed text-accent-900 md:text-lg"
                      >
                        {block.text}
                      </blockquote>
                    );
                  }
                  return (
                    <p key={`${block.type}-${index}`} className="mt-4 text-base leading-[1.9] text-foreground-800">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-9 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-xs text-foreground-500">相关标签：</span>
                {item.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/forecast?q=${encodeURIComponent(tag)}`}
                    className="rounded-md bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <p className="mt-6 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1" />
                本文基于公开信息整理，描述的是正在发酵的迹象而非结论。如发现内容有误，欢迎通过关于页面的联系方式告诉我们。
              </p>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">热度排行榜</h2>
                  <ol className="mt-4 divide-y divide-background-200">
                    {hotList.map((entry, index) => (
                      <li key={entry.id}>
                        <Link to={`/forecast/${entry.id}`} className="group flex items-start gap-3 py-3">
                          <span
                            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                              index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className="text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                            {entry.title}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="mt-5 rounded-lg bg-primary-500 p-5 text-background-50">
                  <h2 className="font-heading text-base font-bold">蹲守这条线</h2>
                  <p className="mt-2 text-sm leading-relaxed text-primary-50">
                    订阅蹲守提醒，事件有新的进展时，我们会第一时间告诉你。
                  </p>
                  <Link
                    to="/#subscribe"
                    className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-4 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-secondary-200"
                  >
                    <i className="ri-notification-3-line" />
                    去订阅
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">相关前瞻推荐</h2>
            <Link to="/forecast" className="whitespace-nowrap text-sm font-semibold text-primary-600 hover:text-primary-500">
              查看全部
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            {relatedList.map((entry, index) => (
              <Reveal key={entry.id} delay={index * 80}>
                <ForecastCard item={entry} variant="horizontal" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}